import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { GlobalPanicAlertService } from 'src/app/services/global-panic-alert.service';
import { PanicAlertData } from 'src/app/services/websocket.service';

@Component({
  selector: 'app-global-panic-alert',
  template: `
    <div *ngIf="activePanicAlert" class="global-panic-alert">
      <div class="container-fluid">
        <div class="row align-items-center py-2">
          <div class="col-auto">
            <div class="alert-icon">
              <i class="fas fa-exclamation-triangle"></i>
            </div>
          </div>
          <div class="col">
            <div class="alert-title">¡ALERTA DE PÁNICO ACTIVA!</div>
            <div class="alert-details">
              <strong>{{ activePanicAlert.guard_name }}</strong> en <strong>{{ activePanicAlert.facility_name }}</strong>
            </div>
          </div>
          <div class="col-auto">
            <div class="d-flex align-items-center gap-2">
              <button *ngIf="!isInSecurityCenter" class="btn btn-light btn-sm" (click)="goToSecurityCenter()">
                <i class="fas fa-shield-alt me-1"></i>
                Centro de Seguridad
              </button>
              <button *ngIf="isInSecurityCenter" class="btn btn-warning btn-sm" (click)="openPanicModal()">
                <i class="fas fa-exclamation-triangle me-1"></i>
                Gestionar Emergencia
              </button>
              <button class="btn btn-outline-light btn-sm" (click)="dismissAlert()" title="Ocultar alerta">
                <i class="fas fa-times"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .global-panic-alert {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      z-index: 10000;
      background: linear-gradient(90deg, #dc3545 0%, #e74c3c 100%);
      color: white;
      box-shadow: 0 2px 10px rgba(220, 53, 69, 0.3);
      border-bottom: 3px solid #c82333;
      animation: slideDown 0.5s ease-out;
    }

    .alert-icon {
      font-size: 28px;
      color: #fff;
      animation: pulse 1.5s infinite;
      text-shadow: 0 0 10px rgba(255, 255, 255, 0.5);
    }

    .alert-title {
      font-weight: 700;
      font-size: 18px;
      margin-bottom: 2px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
    }

    .alert-details {
      font-size: 14px;
      opacity: 0.95;
      margin: 0;
    }

    .btn {
      border: none;
      padding: 8px 16px;
      border-radius: 6px;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.3s ease;
      text-transform: uppercase;
      letter-spacing: 0.3px;
    }

    .btn-light {
      background: #fff;
      color: #dc3545;
      border: 2px solid #fff;
    }

    .btn-light:hover {
      background: #f8f9fa;
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    }

    .btn-outline-light {
      background: transparent;
      color: white;
      border: 2px solid rgba(255, 255, 255, 0.7);
      width: 36px;
      height: 36px;
      padding: 0;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .btn-outline-light:hover {
      background: rgba(255, 255, 255, 0.2);
      border-color: white;
      transform: scale(1.1);
    }

    @keyframes slideDown {
      from {
        transform: translateY(-100%);
        opacity: 0;
      }
      to {
        transform: translateY(0);
        opacity: 1;
      }
    }

    @keyframes pulse {
      0%, 100% { 
        transform: scale(1);
        opacity: 1;
      }
      50% { 
        transform: scale(1.1);
        opacity: 0.8;
      }
    }

    /* Responsive adjustments */
    @media (max-width: 768px) {
      .alert-title {
        font-size: 16px;
      }
      .alert-details {
        font-size: 12px;
      }
      .btn {
        font-size: 11px;
        padding: 6px 12px;
      }
    }
  `]
})
export class GlobalPanicAlertComponent implements OnInit, OnDestroy {
  activePanicAlert: PanicAlertData | null = null;
  isInSecurityCenter = false;
  private subscription: Subscription = new Subscription();

  constructor(
    private globalPanicAlertService: GlobalPanicAlertService,
    private router: Router
  ) {}

  ngOnInit(): void {
    console.log('🚨 GLOBAL COMPONENT: Inicializando componente global');
    
    // Monitor route changes
    this.subscription.add(
      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd)
      ).subscribe((event: NavigationEnd) => {
        this.isInSecurityCenter = event.url.includes('/central-de-seguridad');
        console.log('🗺 GLOBAL: En centro de seguridad:', this.isInSecurityCenter);
      })
    );
    
    // Check initial route
    this.isInSecurityCenter = this.router.url.includes('/central-de-seguridad');
    
    this.subscription.add(
      this.globalPanicAlertService.activePanicAlert$.subscribe(
        alert => {
          console.log('🚨 GLOBAL COMPONENT: Alerta recibida en componente:', alert);
          const wasActive = !!this.activePanicAlert;
          this.activePanicAlert = alert;
          
          if (alert) {
            console.log('✅ GLOBAL COMPONENT: Mostrando banner de alerta');
            // If we're in security center and this is a new alert, open modal
            if (this.isInSecurityCenter && !wasActive) {
              this.openPanicModal();
            }
          } else {
            console.log('❌ GLOBAL COMPONENT: Ocultando banner de alerta');
          }
        }
      )
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  goToSecurityCenter(): void {
    this.router.navigate(['/central-de-seguridad']);
  }

  dismissAlert(): void {
    this.globalPanicAlertService.dismissAlert();
  }

  openPanicModal(): void {
    // Emit event to security center to open panic modal
    (window as any).openPanicAlertModal?.();
  }
}